import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Button, Col, FormCheckbox, Heading, IconEdit, InPageAlert } from '@snsw-gel/react';
import { createEventBooking } from 'app/services/api/internal/API';
import { useEventFlow } from 'app/contexts/EventFlowContext';
import EventLayout from './EventLayout';
import { StyledBackButton, StyledRow, SectionHeader, ReviewValue, ReviewItem } from './EventReview.styled';

interface EventReviewProps {
  agencyName: string;
}

interface ReviewForm {
  consent: boolean;
  privacyConsent: boolean;
  atsiConsent: boolean;
  requireSmsReminder: boolean;
}

interface EventInfo {
  title?: string;
  eventStartTime?: string;
  eventEndTime?: string;
  location?: string;
  send_customer_sms?: boolean;
}

// Labels and display order for business detail fields.
// Fields not listed here (e.g. identifier, identifierType) are excluded from the review.
const BUSINESS_DETAIL_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'businessName', label: 'Business name' },
  { key: 'businessAddress', label: 'Business address' },
  { key: 'businessStage', label: 'What stage is your business at?' },
  { key: 'role', label: 'What is your role in the business? ' },
  { key: 'numberOfEmployees', label: 'How many people work in your business?' },
  { key: 'businessIdentity', label: 'Does your business identify as any of the following?' },
  { key: 'businessIndustry', label: 'What industry does your business operate in?' },
];

const formatEventDateTime = (startTime?: string, endTime?: string): string => {
  if (!startTime) return '';
  const start = new Date(startTime);
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  let formatted = start.toLocaleDateString('en-AU', options);
  if (endTime) {
    const end = new Date(endTime);
    const endFormatted = end.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    formatted += ` - ${endFormatted}`;
  }
  return formatted;
};

const EventReview: React.FC<EventReviewProps> = ({ agencyName }) => {
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { personalDetails, businessDetails, eventDetails, setBookingConfirmation, eventId } = useEventFlow();
  const eventInfo: EventInfo | null = eventDetails;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ReviewForm>({
    defaultValues: {
      consent: false,
      privacyConsent: false,
      atsiConsent: false,
      requireSmsReminder: false,
    },
  });

  //clean businessDetails: drop empty values and convert businessIdentity options to a string
  const cleanedBusinessDetails: Record<string, string> = Object.fromEntries(
    Object.entries(businessDetails ?? {})
      .map(([key, value]) =>
        key === 'businessIdentity' && Array.isArray(value)
          ? [
              key,
              value
                .map((option) => option.label)
                .filter(Boolean)
                .join(', '),
            ]
          : [key, value],
      )
      .filter(([, value]) => value !== null && value !== undefined && value !== ''),
  );

  const onSubmit = async (formData: ReviewForm) => {
    setSubmitting(true);
    setError(null);

    try {
      const showSmsReminder = eventInfo?.send_customer_sms === true;

      const responseData = await createEventBooking(eventId, {
        data: {
          firstName: personalDetails?.firstName || '',
          lastName: personalDetails?.lastName || '',
          email: personalDetails?.email || '',
          phone: personalDetails?.phone || '',
          ...(showSmsReminder ? { requireSmsReminder: formData.requireSmsReminder } : {}),
          ...cleanedBusinessDetails,
        },
      });

      if (responseData) {
        const bookingData = responseData?.data || responseData;
        setBookingConfirmation(bookingData);
        history.push(`/events/${agencyName}/confirmation`);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } catch (err: any) {
      const status = err?.response?.status;

      // The booking-create endpoint only ever responds with 409 when the event is
      // fully booked (capacity reached). We therefore treat every 409 as "fully
      // booked" and send the customer back to the event landing page with an
      // isFullyBooked flag, where the friendly "fully booked" message is shown.
      //
      // We deliberately do NOT match on the response message: Apigee sometimes
      // swallows the backend's "Event is fully booked" text and returns a generic
      // body (e.g. "unhandled_error"), so relying on the message would miss those
      // cases and leak a technical string to the customer.
      if (status === 409) {
        const search = eventId ? `?eventCode=${encodeURIComponent(eventId)}&isFullyBooked=true` : '?isFullyBooked=true';
        history.push(`/events/${agencyName}/landing${search}`);
        return;
      }

      if (status === 400) {
        setError('This event is no longer available for booking.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onBack = () => {
    history.push(`/events/${agencyName}/business-details`);
  };

  if (!personalDetails || !businessDetails) {
    history.push(`/events/${agencyName}/details`);
    return null;
  }

  return (
    <EventLayout currentStep={3}>
      <Heading level={1}>Registration details</Heading>

      {error && (
        <InPageAlert variant="error" title="Booking failed">
          <p>{error}</p>
        </InPageAlert>
      )}

      {/* Event Details */}
      <section>
        <SectionHeader>
          <Heading level={2}>Event details</Heading>
        </SectionHeader>
        <ReviewItem>
          <Heading level={5}>Event name</Heading>
          <ReviewValue>{eventInfo?.title || '—'}</ReviewValue>
        </ReviewItem>
        <ReviewItem>
          <Heading level={5}>Date and time</Heading>
          <ReviewValue>{formatEventDateTime(eventInfo?.eventStartTime, eventInfo?.eventEndTime) || '—'}</ReviewValue>
        </ReviewItem>
        <ReviewItem>
          <Heading level={5}>Location</Heading>
          <ReviewValue>{eventInfo?.location || 'Online'}</ReviewValue>
        </ReviewItem>
        <hr />
      </section>

      {/* Personal Details Review */}
      <section>
        <SectionHeader>
          <Heading level={2}>Personal details</Heading>
          <Button variant="link" onClick={() => history.push(`/events/${agencyName}/details`)} iconStart={<IconEdit />}>
            Edit
          </Button>
        </SectionHeader>
        <ReviewItem>
          <Heading level={5}>Given name</Heading>
          <ReviewValue>{personalDetails.firstName}</ReviewValue>
        </ReviewItem>
        <ReviewItem>
          <Heading level={5}>Family name</Heading>
          <ReviewValue>{personalDetails.lastName}</ReviewValue>
        </ReviewItem>
        <ReviewItem>
          <Heading level={5}>Email</Heading>
          <ReviewValue>{personalDetails.email}</ReviewValue>
        </ReviewItem>
        <ReviewItem>
          <Heading level={5}>Phone</Heading>
          <ReviewValue>{personalDetails.phone}</ReviewValue>
        </ReviewItem>
        <hr />
      </section>

      {/* Business Details Review */}
      <section>
        <SectionHeader>
          <Heading level={2}>Business details</Heading>
          <Button
            variant="link"
            onClick={() => history.push(`/events/${agencyName}/business-details`)}
            iconStart={<IconEdit />}
          >
            Edit
          </Button>
        </SectionHeader>
        {BUSINESS_DETAIL_FIELDS.filter(({ key }) => key in cleanedBusinessDetails).map(({ key, label }) => (
          <ReviewItem key={key}>
            <Heading level={5}>{label}</Heading>
            <ReviewValue>{cleanedBusinessDetails[key]}</ReviewValue>
          </ReviewItem>
        ))}
        <hr />
      </section>

      {/* Consent */}
      <section style={{ marginTop: '1.5rem' }}>
        <p>Email notifications will be sent to confirm and remind you about this event.</p>
      </section>

      <form onSubmit={handleSubmit(onSubmit)}>
        {eventInfo?.send_customer_sms === true && (
          <section style={{ marginTop: '1rem' }}>
            <Heading level={3}>Notifications</Heading>
            <Controller
              name="requireSmsReminder"
              control={control}
              render={({ field }) => (
                <FormCheckbox
                  id="requireSmsReminder"
                  label="I would also like to receive SMS notifications for this appointment (optional)."
                  checked={field.value}
                  value="Yes"
                  onChange={(value) => field.onChange(value === 'Yes')}
                  name="requireSmsReminder"
                />
              )}
            />
          </section>
        )}

        <section style={{ marginTop: '1rem' }}>
          <Heading level={3}>Consent</Heading>

          <Controller
            name="consent"
            control={control}
            rules={{
              validate: (value) => value === true || 'You must accept this consent to confirm your registration.',
            }}
            render={({ field }) => (
              <FormCheckbox
                id="consent"
                label="I understand and agree that Service NSW, its external business providers and their business advisors, accept no responsibility, and will accept no liability for the accuracy, currency, reliability or correctness of any advice, recommendation or information provided by an external business provider or advisor to me in the delivery of Small Business Advisory services."
                errorMessage={errors.consent?.message}
                hasError={!!errors.consent}
                checked={field.value}
                value="Yes"
                onChange={(value) => field.onChange(value === 'Yes')}
                name="Consent"
              />
            )}
          />

          <Controller
            name="atsiConsent"
            control={control}
            rules={{
              validate: (value) => value === true || 'You must accept this consent to confirm your registration.',
            }}
            render={({ field }) => (
              <FormCheckbox
                id="atsiConsent"
                label="I agree to Service NSW sharing my personal information, including my Aboriginal or Torres Strait Islander heritage status and information, with external service providers and their business advisors, in order to provide me with business advisory services."
                errorMessage={errors.atsiConsent?.message}
                hasError={!!errors.atsiConsent}
                checked={field.value}
                value="Yes"
                onChange={(value) => field.onChange(value === 'Yes')}
                name="ATSI Consent"
              />
            )}
          />
        </section>

        <StyledRow>
          <Col>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Register'}
            </Button>
          </Col>
        </StyledRow>
        <StyledRow>
          <Col>
            <StyledBackButton variant="back" onClick={onBack} type="button">
              Back
            </StyledBackButton>
          </Col>
        </StyledRow>
      </form>
    </EventLayout>
  );
};

export default EventReview;
